/**
 * Local-player facing indicator: a clean, glowing white chevron just ahead of
 * your tank (à la a Mobile Legends / iOS direction arrow) pointing in the given
 * heading. Replaces the old dashed highlight ring so you can find yourself and
 * read your facing at a glance.
 *
 * Drawn in the tank-local frame (origin at the tank center) — the caller has
 * already translated to the tank but NOT rotated. `angle` is the world heading
 * to point at (the hull's `bodyAngle`). Self-contained: saves/restores its own
 * context state (rotation, shadow, stroke), so it composes with any theme.
 */
export function drawLocalIndicator(
  ctx: CanvasRenderingContext2D,
  r: number,
  angle: number,
  nowMs: number
): void {
  // Gentle breathing shine so it reads as "alive" without flickering.
  const pulse = 0.8 + 0.2 * Math.sin(nowMs / 320);

  ctx.save();
  ctx.rotate(angle); // +x now points along the heading
  ctx.lineCap = "round";
  ctx.lineJoin = "round"; // softens the tip a touch — clean, not a hard arrow
  ctx.shadowColor = "rgba(255, 255, 255, 0.9)";
  ctx.shadowBlur = 8;

  const back = r + 6; // where the chevron arms sit
  const tip = r + 17; // the pointed tip, out ahead of the hull
  const wid = r * 0.72; // arm half-height

  // One connected chevron: arm → point → arm. A single stroked path, pointy at
  // the tip but with soft joins/caps.
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.95 * pulse})`;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(back, -wid);
  ctx.lineTo(tip, 0);
  ctx.lineTo(back, wid);
  ctx.stroke();

  ctx.restore();
}
